/**
 * Resident API Service
 * Handles all API calls for Resident (Patient) Management
 */

import apiClient from './apiClient';
import {
  ResidentDTO,
  CreateResidentRequest,
  UpdateResidentRequest,
  PageResponse,
} from '../types/resident.types';

const BASE_PATH = '/v1/residents';

/**
 * Resident Service
 * Provides type-safe API calls for resident management
 */
class ResidentService {
  /**
   * Create a new resident (admit patient)
   */
  async createResident(request: CreateResidentRequest): Promise<ResidentDTO> {
    const response = await apiClient.post<ResidentDTO>(BASE_PATH, request);
    return response.data;
  }

  /**
   * Get resident by ID
   */
  async getResidentById(id: string): Promise<ResidentDTO> {
    const response = await apiClient.get<ResidentDTO>(`${BASE_PATH}/${id}`);
    return response.data;
  }

  /**
   * Get all residents (paginated)
   */
  async getAllResidents(
    page: number = 0,
    size: number = 20
  ): Promise<PageResponse<ResidentDTO>> {
    const response = await apiClient.get<PageResponse<ResidentDTO>>(BASE_PATH, {
      params: { page, size },
    });
    return response.data;
  }

  /**
   * Search residents by name
   */
  async searchResidents(
    query: string,
    page: number = 0,
    size: number = 20
  ): Promise<PageResponse<ResidentDTO>> {
    const response = await apiClient.get<PageResponse<ResidentDTO>>(
      `${BASE_PATH}/search`,
      {
        params: { q: query, page, size },
      }
    );
    return response.data;
  }

  /**
   * Update resident information
   */
  async updateResident(
    id: string,
    request: Partial<ResidentDTO>
  ): Promise<ResidentDTO> {
    const response = await apiClient.patch<ResidentDTO>(
      `${BASE_PATH}/${id}`,
      request
    );
    return response.data;
  }

  /**
   * Discharge resident from facility
   */
  async dischargeResident(
    id: string,
    reason: string
  ): Promise<ResidentDTO> {
    const response = await apiClient.post<ResidentDTO>(
      `${BASE_PATH}/${id}/discharge`,
      null,
      {
        params: { reason },
      }
    );
    return response.data;
  }

  /**
   * Delete resident (soft delete/archive)
   */
  async deleteResident(id: string): Promise<void> {
    await apiClient.delete(`${BASE_PATH}/${id}`);
  }

  /**
   * Get active resident count for a facility
   */
  async getActiveResidentCount(facilityId: string): Promise<number> {
    const response = await apiClient.get<number>(
      `${BASE_PATH}/facilities/${facilityId}/count`
    );
    return response.data;
  }

  // ========== Helper Methods ==========

  /**
   * Calculate age from date of birth
   */
  calculateAge(dateOfBirth: string): number {
    const birth = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }

    return age;
  }

  /**
   * Format full name
   */
  getFullName(resident: ResidentDTO): string {
    const { firstName, middleName, lastName } = resident;
    return middleName
      ? `${firstName} ${middleName} ${lastName}`
      : `${firstName} ${lastName}`;
  }

  /**
   * Get room assignment display
   */
  getRoomAssignment(resident: ResidentDTO): string {
    const { roomNumber, bedNumber } = resident;
    if (!roomNumber) return 'Not Assigned';
    return bedNumber ? `Room ${roomNumber}, Bed ${bedNumber}` : `Room ${roomNumber}`;
  }

  /**
   * Check if resident is active
   */
  isActive(resident: ResidentDTO): boolean {
    return resident.status === 'ACTIVE' && resident.isActive !== false;
  }

  /**
   * Get status display color
   */
  getStatusColor(status: string): 'success' | 'error' | 'warning' | 'info' | 'default' {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'DISCHARGED':
        return 'info';
      case 'DECEASED':
        return 'error';
      case 'TRANSFERRED':
        return 'warning';
      case 'ON_LEAVE':
        return 'warning';
      default:
        return 'default';
    }
  }

  /**
   * Get care level display color
   */
  getCareLevelColor(careLevel: string): 'error' | 'warning' | 'info' | 'success' {
    switch (careLevel) {
      case 'CRITICAL':
        return 'error';
      case 'HIGH':
        return 'warning';
      case 'MEDIUM':
        return 'info';
      case 'LOW':
        return 'success';
      default:
        return 'info';
    }
  }
}

// Export singleton instance
export default new ResidentService();
